import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ChartingLibraryFeatureset,
  EntityId,
  IBasicDataFeed,
  // IChartWidgetApi,
  IChartingLibraryWidget,
  IOrderLineAdapter,
  LibrarySymbolInfo,
  ResolutionString,
  SeriesFormat,
  ThemeName,
  Timezone,
  widget,
} from '../../../../public/static/charting_library';
import { Markets, OHLCBlock, RealtimeUpdate } from '../../../types/market.types';
import {
  ChartElementSVG,
  ChartTypeSelectionDD,
} from '../../../components/tradingView/ChartTypeSelectionDD';
import { ChartTypePersistedAtom, useCexSocketConnected, useCurrentPrice, useDarkMode, useExoticMode, useIsExRoute } from '../../../recoil';
import {
  getOslonTimezone,
} from '../../../utils/dates/displayDateTime';
import {
  buyPriceAtom,
  chartControlsSettingsAtom,
  queuets2priceAtom,
  rerenderPositionAtom,
  sellPriceAtom,
  visualizeddAtom,
  market2resolutionAtom,
  indicatorCountAtom,
  drawingAtom,
} from '../../../recoil';
import { TradeType } from '../../../types/trade.type';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { TradingDataService } from '../../../services/trading-data.service';
import { useParams } from 'react-router-dom';
import {
  formatResolution,
  isntAvailable,
  PRICE_PROVIDER,
  sleep,
  SUPPORTED_RESOLUTIONS,
  timeDeltaMapping
} from './helpers';
import { useOrdersData } from '../../../recoil/hooks/userOrderState';
import { connectSocket } from '../../../services/websocket-client.service';
import { getKlineData, klineDataResponseToOHLCData, klineDataToOHLCData } from '../../../services/cex-engine.service';
import { getCexEngineSocket } from '../../../services/cex-engine-socket.service';

// clean
const defaults = {
  priceProvider: PRICE_PROVIDER,
  cssPath: `/tv.css`,
  upRectangeColor: 'rgba(55, 114, 255, 0.04)',
  upLineColor: 'rgba(55, 114, 255, 1)',
  downRectangeColor: 'rgba(255, 104, 104, 0.04)',
  downLineColor: 'rgba(255, 104, 104, 1)',

  library_path: `/static/charting_library/`,
  theme: 'Dark',
  interval: '1' as ResolutionString,
  basicDisabled: [
    'header_compare',
    'header_symbol_search',
    'header_widget',
    'go_to_date',
    'display_market_status',
  ],

  confgis: {
    SUPPORTED_RESOLUTIONS,
    exchanges: [
      {
        // `exchange` argument for the `searchSymbols` method, if a user selects this exchange
        value: PRICE_PROVIDER,

        // filter name
        name: PRICE_PROVIDER,

        // full exchange name displayed in the filter popup
        desc: PRICE_PROVIDER,
      },
    ],
    symbols_types: [
      {
        name: 'Crypto',
        // `symbolType` argument for the `searchSymbols` method, if a user selects this symbol type
        value: 'Crypto',
      },
      {
        name: 'Currencies',
        // `symbolType` argument for the `searchSymbols` method, if a user selects this symbol type
        value: 'Currencies',
      },
      // ...
    ],
  },
  BG: 'rgb(224, 79, 0)',
  upIcon: '▲',
  downIcon: '▼',
  green: 'rgb(108, 211, 173)',
  red: 'rgb(255, 104, 104)',
};


// These atoms are now defined in Recoil - importing from recoil/atoms/chartAtoms
const dataService = new TradingDataService();

export const MultiResolutionChart = ({
  market: marke,
  isMobile,
  index,
}: {
  market: Markets;
  index: number;
  isMobile?: boolean;
}) => {
  const isExotic = useIsExRoute();
  const [darkMode, setDarkMode] = useDarkMode();
  const [currentOpenOrders, setCurrentOpenOrders] = useOrdersData();
  // console.log(currentOpenOrders, 'currentOpenOrderscurrentOpenOrders');
  const { market: marketParam } = useParams<{ market: string; }>();
  const market = marke.replace('-', '');
  const [market2resolution, setMarket2resolution] = useRecoilState(
    market2resolutionAtom
  );
  const settings = useRecoilValue(chartControlsSettingsAtom);
  const [currentPrice, setCurrentPrice] = useCurrentPrice();
  const chartId = market + index;
  const [chartReady, setChartReady] = useState<boolean>(false);
  const lastSyncedKline = useRef<{ [asset: string]: OHLCBlock; }>({});
  const websocketTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const disconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [isCexSocketConnected] = useCexSocketConnected();
  const trade2visualisation = useRef<
    {
      positionRef: IOrderLineAdapter;
      option: TradeType;
    }[]
  >([]);

  const indicatorCount = useRecoilValue(indicatorCountAtom);
  let realTimeUpdateRef = useRef<RealtimeUpdate | null>(null);
  let widgetRef = useRef<IChartingLibraryWidget | null>(null);
  const containerDivRef = useRef<HTMLDivElement>(null);
  const [drawing, setDrawing] = useRecoilState(drawingAtom);
  const chartType = useRecoilValue(ChartTypePersistedAtom);
  const setChartType = useSetRecoilState(ChartTypePersistedAtom);
  const wsConnectSymbolRef = useRef('');

  // Label references for management
  const buyPriceLabelsRef = useRef<Map<string, EntityId>>(new Map());
  const sellPriceLabelsRef = useRef<Map<string, EntityId>>(new Map());
  // Helper function to generate unique keys for orders
  const getOrderKey = (order: any) => {
    return `${order.side}_${order.price}_${order.id || order.timestamp}`;
  };

  const datafeed: IBasicDataFeed = useMemo(() => {
    return {
      onReady: (callback) => {
        // @ts-ignore
        setTimeout(() => callback(defaults.confgis));
      },
      searchSymbols: async (
        userInput,
        exchange,
        symbolType,
        onResultReadyCallback
      ) => {
        onResultReadyCallback([]);
      },
      resolveSymbol: async (
        symbolName,
        onSymbolResolvedCallback,
        onResolveErrorCallback
      ) => {
        const symbolInfo: LibrarySymbolInfo = {
          ticker: marketParam || '', // symbol
          name: marketParam || '', // name
          description: '',
          type: 'Crypto', // Crypto
          session: '24x7',
          exchange: PRICE_PROVIDER,
          minmov: 1,
          pricescale: 100,
          has_intraday: true,
          has_seconds: true,
          visible_plots_set: 'ohlc',
          seconds_multipliers: ['1', '10'],
          has_weekly_and_monthly: true,
          supported_resolutions: SUPPORTED_RESOLUTIONS as ResolutionString[],
          volume_precision: 2,
          data_status: 'streaming',
          timezone: getOslonTimezone() as Timezone,
          listed_exchange: PRICE_PROVIDER,
          format: 'price' as SeriesFormat,
        };

        onSymbolResolvedCallback(symbolInfo);
      },
      getBars: async (
        symbolInfo,
        resolution,
        periodParams,
        onHistoryCallback,
        onErrorCallback
      ) => {
        try {
          const { from, to, firstDataRequest } = periodParams;
          const { name, ticker } = symbolInfo as {
            name: string;
            ticker: string;
          };

          if (firstDataRequest) {
            lastSyncedKline.current = {};
          }


          const ohlc = isExotic ?
            (await getKlineData(ticker, '1m', {
              startTime: from * 1000,
              endTime: to * 1000,
            }).then(res => res)
              .catch(err => []))
              .map(klineDataResponseToOHLCData)
            : await dataService.getCandleData(
              ticker,
              '1min',
              from,
              to
            );

          // Mark the last synced kline, can be used to generate new klines by just using price ticks
          if (firstDataRequest && ohlc.length) {
            lastSyncedKline.current[ticker + timeDeltaMapping(resolution)] = ohlc[ohlc.length - 1];
          }

          // Load inital data
          onHistoryCallback(ohlc, {
            noData: false,
          });

          if (isExotic) {
            websocketTimeoutRef.current && clearTimeout(websocketTimeoutRef.current);
            websocketTimeoutRef.current = setTimeout(() => {
              dataService;
              getCexEngineSocket()?.onKlinesData(data => {
                if (data.symbol !== ticker) return;
                const ohlc = klineDataToOHLCData(data);
                syncTVwithWSOHLC(ohlc);
              });
            }, 1000);
          } else {
            websocketTimeoutRef.current && clearTimeout(websocketTimeoutRef.current);
            websocketTimeoutRef.current = setTimeout(() => {
              // Connect the websocket only once
              if (wsConnectSymbolRef.current !== ticker) {
                wsConnectSymbolRef.current = ticker;
                dataService.connectWebSocket(ticker, (ohlc) => {
                  syncTVwithWSOHLC(ohlc);
                });
              }
            }, 1000);
          }
        } catch (error) {
          console.log('pyth-deb:error-thrown', error);
          onErrorCallback(error as string);
        }
      },
      subscribeBars: (
        symbolInfo,
        resolution,
        onRealtimeCallback,
        _,
        onResetCacheNeededCallback
      ) => {
        realTimeUpdateRef.current = {
          symbolInfo,
          resolution,
          onRealtimeCallback,
          onResetCacheNeededCallback,
        };
      },
      unsubscribeBars: () => console.log,
    };
  }, [marketParam]);

  const [hideVisulizations] = useRecoilState(visualizeddAtom);
  const resolution = (market2resolution?.[chartId] || '1') as ResolutionString;

  useEffect(() => {
    if (isCexSocketConnected && marketParam) {
      getCexEngineSocket()?.subscribeKlines({
        symbol: marketParam || '',
        interval: '1m'
      });
    }
    return () => {
      if (isCexSocketConnected && marketParam) {
        getCexEngineSocket()?.unsubscribeKlines(marketParam || '');
      }
    };
  }, [marketParam, isCexSocketConnected]);

  useEffect(() => {
    try {
      const chart = new widget({
        datafeed,
        interval: defaults.interval,
        timeframe: '200',
        locale: 'en',
        container: containerDivRef.current!,
        library_path: defaults.library_path,
        custom_css_url: defaults.cssPath,
        timezone: getOslonTimezone() as Timezone,
        symbol: marketParam,
        theme: (darkMode ? 'dark' : 'light') as ThemeName,
        enabled_features: ['header_saveload', 'hide_left_toolbar_by_default'],
        load_last_chart: true,
        time_frames: [
          {
            text: '1D',
            resolution: '30' as ResolutionString,
            description: '1 Day look back',
            title: '1D',
          },
          {
            text: '4H',
            resolution: '5' as ResolutionString,
            description: '4 Hour look back',
            title: '4H',
          },
          {
            text: '1H',
            resolution: '1' as ResolutionString,
            description: '1 Hour look back',
            title: '1H',
          },
          {
            text: '30',
            resolution: '1' as ResolutionString,
            description: '30 Minute look back',
            title: '30Min',
          },
          {
            text: '10',
            resolution: '1S' as ResolutionString,
            description: '10 Minute look back',
            title: '10Min',
          },
        ],
        saved_data: drawing?.[chartId],
        disabled_features:
          (window.innerWidth < 600
            ? ['left_toolbar', ...defaults.basicDisabled]
            : [...defaults.basicDisabled]) as ChartingLibraryFeatureset[],
      });

      chart.onChartReady(() => {
        setChartReady(true);
      });

      widgetRef.current = chart;
    } catch (e) {
      console.log('[chart-deb]-err', e);
    }

    return () => {
      widgetRef.current?.remove();
      widgetRef.current = null;
      setChartReady(false);
    };
  }, [marketParam]);

  useEffect(() => {
    // Debounced disconnect when market param changes
    disconnectTimeoutRef.current && clearTimeout(disconnectTimeoutRef.current);
    disconnectTimeoutRef.current = setTimeout(() => {
      if (marketParam) {
        dataService.disconnectAllWebSocketsExcept(marketParam);
      }
    }, 1500); // 1.5 second debounce

    return () => {
      if (disconnectTimeoutRef.current) {
        clearTimeout(disconnectTimeoutRef.current);
      }
    };
  }, [marketParam, isExotic])
  

  useEffect(() => {
    const conn = connectSocket();
    const pingInt = setInterval(() => { conn?.ping(); }, 18000);
    return () => {
      conn?.socket.disconnect();
      clearInterval(pingInt);
    };
  }, []);

  useEffect(() => {
    if (!chartReady) return;
    widgetRef.current?.activeChart().removeAllShapes();
  }, [chartReady]);

  const priceCache = useRecoilValue(queuets2priceAtom);

  const syncTVwithWSOHLC = async (ohlc: OHLCBlock) => {
    if (typeof realTimeUpdateRef.current?.onRealtimeCallback != 'function') return;
    realTimeUpdateRef.current.onRealtimeCallback(ohlc);
    lastSyncedKline.current[ohlc.time] = ohlc;
    setCurrentPrice({ value: ohlc.close, ...ohlc, marketParam: marketParam! });
  };

  const rerenderPostion = useRecoilValue(rerenderPositionAtom);


  const createPriceLabels = () => {
    if (!chartReady || !widgetRef.current || !currentOpenOrders) return;

    const chart = widgetRef.current.activeChart();
    if (!chart) return;

    // Clear existing labels
    removeAllPriceLabels();

    // Create labels for each order
    currentOpenOrders?.filter(or => or?.symbol == marketParam).forEach((order) => {
      if (!order.price) return;

      try {
        const priceNum = parseFloat(order.price);
        const orderKey = getOrderKey(order);

        const isBuy = order.side === 'buy';
        const labelRef = isBuy ? buyPriceLabelsRef : sellPriceLabelsRef;
        // console.log(orderKey, 'orderKeyorderKeyorderKey')
        if (labelRef.current.has(orderKey)) return; // Already exists
        // console.log("new one")
        const labelId = chart.createShape(
          { time: Date.now() / 1000, price: priceNum },
          {
            shape: 'horizontal_line',
            overrides: {
              linecolor: isBuy ? defaults.green : defaults.red,
              linewidth: 2,
              linestyle: 0,
              showLabel: true,
              frozen: true,
              textcolor: isBuy ? defaults.green : defaults.red,
              horzLabelsAlign: isBuy ? 'right' : 'left',
              vertLabelsAlign: 'middle',
            },
            text: `${isBuy ? 'Buy' : 'Sell'}: ${priceNum.toFixed(4)}`,
          }
        );

        if (labelId !== null) {
          labelRef.current.set(orderKey, labelId);
          // console.log(`${isBuy ? 'Buy' : 'Sell'} price label created at:`, priceNum);
        }
      } catch (error) {
        console.error('Error creating price label:', error);
      }
    });
  };

  const removeAllPriceLabels = () => {
    if (!widgetRef.current) return;

    const chart = widgetRef.current.activeChart();
    if (!chart) return;

    // Remove all buy price labels
    buyPriceLabelsRef.current.forEach((labelId) => {
      try {
        chart.removeEntity(labelId);
      } catch (error) {
        console.error('Error removing buy price label:', error);
      }
    });
    buyPriceLabelsRef.current.clear();

    // Remove all sell price labels
    sellPriceLabelsRef.current.forEach((labelId) => {
      try {
        chart.removeEntity(labelId);
      } catch (error) {
        console.error('Error removing sell price label:', error);
      }
    });
    sellPriceLabelsRef.current.clear();
  };

  const removePriceLabel = (orderKey: string) => {
    if (!widgetRef.current) return;

    const chart = widgetRef.current.activeChart();
    if (!chart) return;

    // Try to remove from both maps
    if (buyPriceLabelsRef.current.has(orderKey)) {
      try {
        chart.removeEntity(buyPriceLabelsRef.current.get(orderKey)!);
        buyPriceLabelsRef.current.delete(orderKey);
      } catch (error) {
        console.error('Error removing buy price label:', error);
      }
    }

    if (sellPriceLabelsRef.current.has(orderKey)) {
      try {
        chart.removeEntity(sellPriceLabelsRef.current.get(orderKey)!);
        sellPriceLabelsRef.current.delete(orderKey);
      } catch (error) {
        console.error('Error removing sell price label:', error);
      }
    }
  };

  const deleteAllPostions = () => {
    trade2visualisation.current.forEach((t) => {
      t.positionRef.remove();
    });
  };

  const renderPositions = async () => {
    deleteAllPostions();
    trade2visualisation.current = [];
  };

  useEffect(() => {
    const timeout = setTimeout(async () => {
      await renderPositions();
    });
    return () => clearTimeout(timeout);
  }, [
    hideVisulizations,
    chartReady,
    priceCache,
    rerenderPostion,
    settings.earlyCloseConfirmation,
    settings.loDragging,
  ]);

  useEffect(() => {
    if (!chartReady) return;
    widgetRef
      .current!.activeChart?.()
      .setChartType((chartType as any)?.[chartId] ?? 1);
  }, [chartType, chartReady]);

  useEffect(() => {
    if (
      chartReady &&
      widgetRef.current &&
      typeof widgetRef.current?.activeChart === 'function'
    ) {
      widgetRef.current.activeChart?.().setResolution(resolution);
    }
  }, [market2resolution, chartReady]);

  const toggleIndicatorDD = (_: any) => {
    widgetRef.current!.activeChart?.().executeActionById('insertIndicator');
  };

  useEffect(() => {
    if (indicatorCount) toggleIndicatorDD('d');
  }, [indicatorCount]);


  useEffect(() => {
    if (!chartReady || !currentOpenOrders) return;

    createPriceLabels();
  }, [currentOpenOrders, chartReady]); // Keep buyPrice/sellPrice if you still need them for something else

  // Clean up on unmount
  useEffect(() => {
    return () => {
      removeAllPriceLabels();
    };
  }, []);

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.remove('light');
    } else {
      document.documentElement.classList.add('light');
    }
  }, [darkMode]);

  useEffect(() => {
    if (chartReady && widgetRef.current) {
      widgetRef.current.changeTheme(darkMode ? 'dark' : 'light');
    }
  }, [darkMode, chartReady]);

  return (
    <div className="flex flex-col w-full h-full">
      {!isMobile ? (
        <div className="items-center justify-between flex-row flex  bg-2 w-full tv-h px-4 ">
          <div className="flex flex-row justify-start font-[500]">
            <div className="ele cursor-pointer">Time</div>
            {SUPPORTED_RESOLUTIONS.map((s) => {
              return (
                <div
                  key={s}
                  onClick={async () => {
                    setMarket2resolution((m: any) => ({
                      ...m,
                      [chartId]: s,
                    }));
                    await sleep(100);
                    realTimeUpdateRef.current?.onResetCacheNeededCallback();
                    widgetRef.current?.activeChart().resetData();
                  }}
                  className={`${s.toLowerCase() == resolution.toLowerCase()
                    && 'active'} ${isntAvailable(s) && 'tb'} ele cursor-pointer`}
                >
                  {formatResolution(s)}
                </div>
              );
            })}
          </div>

          <div className="flex">
            {/* The chart types dropdown */}
            <ChartTypeSelectionDD
              setActive={(updatedType: number) => {
                setChartType((ct: any) => ({ ...ct, [chartId]: updatedType }));
              }}
              active={(chartType as any)[chartId] ?? 1}
            />

            {/* Indicators Popup toggle */}
            <button
              onClick={toggleIndicatorDD}
              className="flex flex-row mr-3 ele text-f12  font-[500] "
            >
              <ChartElementSVG className="mr-[3px]" /> Indicators
            </button>
          </div>

        </div>
      ) : null}
      <div className="w-full  flex-grow">
        <div
          ref={containerDivRef}
          id="chart-element-main"
          className="TVChartContainer w-[100%] h-[100%]"
        />
      </div>
    </div>
  );
};
